import { Injectable, OnDestroy, OnInit } from '@angular/core';
import { BehaviorSubject, ReplaySubject } from 'rxjs';
import { UserDetails } from '../../api-authorization/IUserModel';
import { SessionContext } from '../shared/model/session-context.model';
import { AS365Defaults } from '../shared/model/AS365Defaults.model';
import { Branch } from '../shared/model/Branch.model';
import { HWCInfo } from '../shared/model/HWCInfo.model';
import { Terminal } from '../shared/model/Terminal.model';
import { CleanBedRequest, StartAppointmentRequest, TMaxUpdateInfo } from '../shared/model/TMaxUpdateInfo.model';
import { ResourceStateInfo } from './resource.manager.service';
import { UntilDestroy } from '@ngneat/until-destroy';

export class SystemCommandInfo {
  command = '';
  data: any = null;
}

@UntilDestroy({ checkProperties: true})
@Injectable({
  providedIn: 'root',
})
export class EventService implements OnInit, OnDestroy {
  WebAPIVersion: string = '1.0.0.0';

  onUserLogInComplete: ReplaySubject<UserDetails> = new ReplaySubject<UserDetails>(1, 5000);
  onUserLogOutComplete: ReplaySubject<any> = new ReplaySubject<any>(1, 1000);
  onUserUpdated: ReplaySubject<any> = new ReplaySubject<any>(1, 1000);

  onSettingsChanged: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  onSessionContextChanged: BehaviorSubject<SessionContext> = new BehaviorSubject<SessionContext>(null);

  onDefaultDataChanged: ReplaySubject<AS365Defaults> = new ReplaySubject<AS365Defaults>(1, 3000);

  onBranchChanged: BehaviorSubject<Branch> = new BehaviorSubject<Branch>(null);
  onTerminalChanged: BehaviorSubject<Terminal> = new BehaviorSubject<Terminal>(null);

  onHWCFound: BehaviorSubject<HWCInfo> = new BehaviorSubject<HWCInfo>(null);
  onHWCConnected: BehaviorSubject<HWCInfo> = new BehaviorSubject<HWCInfo>(null);
  onHWCDisconnected: BehaviorSubject<HWCInfo> = new BehaviorSubject<HWCInfo>(null);

  onSystemCommand: BehaviorSubject<SystemCommandInfo> = new BehaviorSubject<SystemCommandInfo>(null);

  onTMaxUpdate: ReplaySubject<TMaxUpdateInfo> = new ReplaySubject<TMaxUpdateInfo>(1, 5000);

  onResetBed: BehaviorSubject<CleanBedRequest> = new BehaviorSubject<CleanBedRequest>(null);

  onStopBed: BehaviorSubject<CleanBedRequest> = new BehaviorSubject<CleanBedRequest>(null);
  onStartBed: BehaviorSubject<StartAppointmentRequest> = new BehaviorSubject<StartAppointmentRequest>(null);
  onOpenTillDrawer: BehaviorSubject<number> = new BehaviorSubject<number>(null);
  onSaveTMaxSessionCounts: BehaviorSubject<void> = new BehaviorSubject<void>(null);
  onRebootHWCDevice: BehaviorSubject<string> = new BehaviorSubject<string>(null);
  onRestartHWCService: BehaviorSubject<string> = new BehaviorSubject<string>(null);

  onAptDataUpdated: ReplaySubject<number> = new ReplaySubject(1, 1000);
  onResourceStateUpdated: ReplaySubject<ResourceStateInfo[]> = new ReplaySubject(1, 10 * 1000);

  onResourceWorkTimeUpdated: BehaviorSubject<any> = new BehaviorSubject<any>(null);

  constructor() {
    console.log('Starting EventService');
  }

  ngOnInit(): void {
    console.log('Init EventService');
  }

  ngOnDestroy(): void {
    console.log('Destroy EventService');
    this.onUserLogInComplete?.unsubscribe();
    this.onUserLogOutComplete?.unsubscribe();
    this.onUserUpdated?.unsubscribe();
    this.onSettingsChanged?.unsubscribe();
    this.onSessionContextChanged?.unsubscribe();
    this.onDefaultDataChanged?.unsubscribe();
    this.onBranchChanged?.unsubscribe();
    this.onTerminalChanged?.unsubscribe();
    this.onHWCFound?.unsubscribe();
    this.onHWCConnected?.unsubscribe();
    this.onHWCDisconnected?.unsubscribe();
    this.onSystemCommand?.unsubscribe();
    this.onTMaxUpdate?.unsubscribe();
    this.onResetBed?.unsubscribe();
    this.onStopBed?.unsubscribe();
    this.onStartBed?.unsubscribe();
    this.onOpenTillDrawer?.unsubscribe();
    this.onSaveTMaxSessionCounts?.unsubscribe();
    this.onRebootHWCDevice?.unsubscribe();
    this.onRestartHWCService?.unsubscribe();
    this.onAptDataUpdated?.unsubscribe();
    this.onResourceStateUpdated?.unsubscribe();
    this.onResourceWorkTimeUpdated?.unsubscribe();
  }

  UserLogInComplete(user: UserDetails) {
    if (user) {
      this.onUserLogInComplete.next(user);
    }
  }

  UserLogOutComplete() {
    this.onUserLogOutComplete.next(undefined);
  }

  UserUpdated() {
    this.onUserUpdated.next(undefined);
  }

  SessionContextChanged(sessionContext: SessionContext) {
    this.onSessionContextChanged.next(sessionContext);
  }

  DefaultDataChanged(defaultData: AS365Defaults) {
    if (defaultData) {
      this.onDefaultDataChanged.next(defaultData);
    }
  }

  BranchChanged(branch: Branch) {
    this.onBranchChanged.next(branch);
  }

  TerminalChanged(terminal: Terminal) {
    this.onTerminalChanged.next(terminal);
  }

  HWCConnected(hwcInfo: HWCInfo) {
    this.onHWCConnected.next(hwcInfo);
  }

  HWCDisconnected() {
    console.log('Disconnected from  HWC on SignalR');
    this.onHWCDisconnected.next(null);
  }

  HWCFound(hwcInfo: HWCInfo) {
    console.log('HWC Found');
    console.log(hwcInfo);
    this.onHWCFound.next(hwcInfo);
  }

  SendSystemCommand(command: string, cmdData: any) {
    const data = new SystemCommandInfo();
    data.command = command;
    data.data = cmdData;
    this.onSystemCommand.next(data);
  }

  TMaxUpdate(info: TMaxUpdateInfo) {
    this.onTMaxUpdate.next(info);
  }

  StopBed(request: CleanBedRequest) {
    this.onStopBed.next(request);
  }

  ResetBed(request: CleanBedRequest) {
    this.onResetBed.next(request);
  }

  StartBed(request: StartAppointmentRequest) {
    this.onStartBed.next(request);
  }

  AptDataUpdated(aptId: number) {
    this.onAptDataUpdated.next(aptId);
  }

  OpenTillDrawer(TillId: number) {
    this.onOpenTillDrawer.next(TillId);
  }

  ResourceStateUpdated(ResourceState: ResourceStateInfo[]) {
    this.onResourceStateUpdated.next(ResourceState);
  }

  SaveTMaxSessionCounts() {
    this.onSaveTMaxSessionCounts.next();
  }

  RebootHWCDevice(BranchCode: string) {
    if (BranchCode) this.onRebootHWCDevice.next(BranchCode);
  }

  RestartHWCService(BranchCode: string) {
    if (BranchCode) this.onRestartHWCService.next(BranchCode);
  }

  ResourceWorkTimeUpdated() {
    this.onResourceWorkTimeUpdated.next(null);
  }
}
